<div class="flex flex-col gap-6" [attr.data-testid]="'unit-form-' + office().id">
  <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
    <app-workforce-kpi-card
      titleKey="project.steps.companyInfo.kpiHcTotal"
      [split]="kpis().headcount"
      testId="unit-kpi-hc"
    />
    <app-workforce-kpi-card
      titleKey="project.steps.companyInfo.kpiFteTotal"
      [split]="kpis().fte"
      digitsInfo="1.0-2"
      testId="unit-kpi-fte"
    />
  </div>

  <app-progress-bar
    [completed]="completedSections()"
    [total]="sectionCount"
    [label]="'project.steps.companyInfo.sectionsComplete' | transloco"
    testIdInfo="unit-progress-info"
    testIdBar="unit-progress-bar"
  />

  <form [formGroup]="form" (submit)="$event.preventDefault()">
    <p-accordion
      [multiple]="true"
      [value]="openPanels()"
      (valueChange)="onOpenPanelsChange($event)"
      data-testid="unit-sections"
    >
      @for (view of sectionViews(); track view.def.name) {
        <p-accordion-panel
          [value]="view.def.name"
          class="section-panel"
          [attr.data-status]="view.status"
          [attr.data-testid]="'section-' + view.def.name"
        >
          <p-accordion-header>
            <div class="flex w-full items-center justify-between gap-4 pr-4">
              <span
                class="text-lg font-semibold"
                [attr.data-testid]="'section-header-' + view.def.name"
              >
                {{ view.def.titleKey | transloco }}
              </span>
              <span class="flex items-center gap-3">
                @if (view.justSaved) {
                  <span
                    class="text-green text-sm"
                    [attr.data-testid]="'section-saved-' + view.def.name"
                  >
                    ✓ {{ 'project.steps.companyInfo.saved' | transloco }}
                  </span>
                }
                <app-section-status-chip
                  [status]="view.status"
                  [testId]="'section-status-' + view.def.name"
                />
              </span>
            </div>
          </p-accordion-header>
          <p-accordion-content>
            <div class="flex flex-col gap-6 pt-2">
              @if (view.def.selectFields.length > 0) {
                <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div class="flex flex-col gap-1">
                    <label class="text-sm font-medium" for="industryId">
                      {{ 'project.steps.industry' | transloco }}
                    </label>
                    <app-directus-select
                      [dropdownKey]="industryDropdownKey"
                      appendTo="body"
                      [projectId]="projectId()"
                      [preloadedDropdown]="industryDropdown()"
                      [selected]="selectedIndustry()"
                      [disabled]="locked()"
                      placeholder="{{ 'project.steps.selectIndustry' | transloco }}"
                      (valueChange)="onIndustrySelected($event)"
                    />
                  </div>
                  @if (tariffRequired()) {
                    <div class="flex flex-col gap-1">
                      <label class="text-sm font-medium" for="tariffId">
                        {{ 'project.steps.tariff' | transloco }}
                      </label>
                      <app-directus-select
                        [dropdownKey]="tariffDropdownKey"
                        appendTo="body"
                        [projectId]="projectId()"
                        [preloadedDropdown]="tariffDropdown()"
                        [filter]="true"
                        [parentValue]="selectedIndustry()"
                        [disabled]="tariffSelectDisabled()"
                        [selected]="selectedTariff()"
                        placeholder="{{ 'project.steps.selectTariff' | transloco }}"
                        (valueChange)="onTariffSelected($event)"
                      />
                    </div>
                  }
                </div>
              }

              @for (category of view.categories; track category.def.key) {
                <div
                  class="flex flex-col gap-2"
                  [attr.data-testid]="'category-' + category.def.key"
                >
                  @if (view.categories.length > 1) {
                    <span class="font-medium">{{ category.def.labelKey | transloco }}</span>
                  }
                  <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <label class="flex flex-col gap-1 text-sm">
                      <span class="text-muted-color">
                        {{ 'project.steps.companyInfo.hcFullTime' | transloco }}
                      </span>
                      <input
                        pInputText
                        appFigureInput="headcount"
                        class="w-full"
                        [formControlName]="category.def.headcountFullTime"
                        [attr.data-testid]="category.def.headcountFullTime + '-input'"
                      />
                    </label>
                    <label class="flex flex-col gap-1 text-sm">
                      <span class="text-muted-color">
                        {{ 'project.steps.companyInfo.hcPartTime' | transloco }}
                      </span>
                      <input
                        pInputText
                        appFigureInput="headcount"
                        class="w-full"
                        [formControlName]="category.def.headcountPartTime"
                        [attr.data-testid]="category.def.headcountPartTime + '-input'"
                      />
                    </label>
                    <label class="flex flex-col gap-1 text-sm">
                      <span class="text-muted-color">
                        {{ 'project.steps.companyInfo.fteManual' | transloco }}
                      </span>
                      <input
                        pInputText
                        appFigureInput="fte"
                        class="w-full"
                        [class.figure-warning]="category.fteWarning"
                        [formControlName]="category.def.fte"
                        [attr.data-testid]="category.def.fte + '-input'"
                        [attr.aria-invalid]="category.fteWarning"
                      />
                      @if (category.fteWarning) {
                        <span
                          class="text-orange text-xs"
                          [attr.data-testid]="category.def.fte + '-warning'"
                        >
                          {{ 'project.steps.companyInfo.fteExceedsHeadcount' | transloco }}
                        </span>
                      }
                    </label>
                  </div>
                </div>
              }

              @if (view.def.countFields.length > 0) {
                <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  @for (field of view.def.countFields; track field) {
                    <label class="flex flex-col gap-1 text-sm">
                      <span class="text-muted-color">{{
                        'project.steps.' + field | transloco
                      }}</span>
                      <input
                        pInputText
                        appFigureInput="headcount"
                        class="w-full"
                        [formControlName]="field"
                        [attr.data-testid]="field + '-input'"
                      />
                    </label>
                  }
                </div>
              }

              @if (view.showSave) {
                <div class="flex justify-end">
                  <app-project-step-save-button
                    [attr.data-testid]="'save-button-' + view.def.name"
                    [loading]="view.saving"
                    [disabled]="view.saving"
                    (clicked)="saveSection(view.def.name)"
                  />
                </div>
              }
            </div>
          </p-accordion-content>
        </p-accordion-panel>
      }
    </p-accordion>
  </form>
</div>
